/**
 * Утилиты для обработки и форматирования ошибок
 */

import { logger } from './logger';

/**
 * Тип ошибки API
 */
export type ApiError = {
  message: string;
  status?: number;
  statusText?: string;
  data?: unknown;
};

/**
 * Класс ошибки API
 */
export class ApiErrorException extends Error {
  status?: number;

  statusText?: string;

  data?: unknown;

  constructor(message: string, status?: number, statusText?: string, data?: unknown) {
    super(message);
    this.name = 'ApiErrorException';
    this.status = status;
    this.statusText = statusText;
    this.data = data;
  }
}

/**
 * Обрабатывает ошибку и возвращает понятное сообщение для пользователя
 * @param error - Ошибка для обработки
 * @returns Сообщение об ошибке для пользователя
 */
export const getErrorMessage = (error: unknown): string => {
  if (error instanceof ApiErrorException) {
    return error.message;
  }

  if (error instanceof Error) {
    // Проверяем, является ли это ошибкой сети
    if (error.message.includes('fetch') || error.message.includes('network')) {
      return 'Ошибка подключения к серверу. Проверьте интернет-соединение.';
    }

    return error.message;
  }

  if (typeof error === 'string') {
    return error;
  }

  return 'Произошла неизвестная ошибка. Попробуйте еще раз.';
};

/**
 * Обрабатывает ответ от API и извлекает сообщение об ошибке
 * @param response - Ответ от fetch
 * @returns Promise с сообщением об ошибке
 */
export const handleApiError = async (response: Response): Promise<string> => {
  try {
    const errorData = (await response.json()) as { error?: string; message?: string };
    return errorData.error || errorData.message || `Ошибка ${response.status}: ${response.statusText}`;
  } catch {
    return `Ошибка ${response.status}: ${response.statusText || 'Unknown error'}`;
  }
};

/**
 * Создает объект ошибки API из ответа
 * @param response - Ответ от fetch
 * @returns Promise с объектом ошибки
 */
export const createApiError = async (response: Response): Promise<ApiErrorException> => {
  const message = await handleApiError(response);
  return new ApiErrorException(message, response.status, response.statusText);
};

/**
 * Логирует ошибку в консоль (только в режиме разработки)
 * @param error - Ошибка для логирования
 * @param context - Контекст, в котором произошла ошибка
 */
export const logError = (error: unknown, context?: string): void => {
  const prefix = context ? `[${context}]` : '';
  logger.error(`${prefix} Error:`, error);

  if (error instanceof Error && error.stack) {
    logger.error(`${prefix} Stack:`, error.stack);
  }
};
